<template>
    <div class="ut-flex ut-flex-col ut-min-h-screen ut-container-lg ut-pb-8">
      <template v-if="isPremier">
        <div class="page-heading listing-referrals">
          <h1>{{ wonListings ? 'Won Listings' : 'Listing referrals' }}</h1>
          <div class="ut-mr-0 ut-hidden md:ut-flex ut-ml-auto">
            <label class="ut-mr-2">Listed in: </label>
            <select-list v-if="timeFrameFilterOptions.length > 0"
                :options="timeFrameFilterOptions"
                v-model="selectedTimeFrame"
                @input="updateTimeFrameFilter"
                data-oa-name="dropdown-time"></select-list>
          </div>
        </div>
        
        <section class="ut-flex-grow ut-w-full ut-pb-10 md:ut-pb-20 ut-card-base">
          <listing-referral-table v-if="listingReferrals"
            :listingreferrals ="listingReferrals ? listingReferrals : null"
            class="ut-px-4 ut-max-w-none lg:ut-max-w-screen-lg"
          />
        <loading-spinner v-else />
        </section>
      </template>
      <template v-else>
        <section class="ut-text-center ut-text-neutral-100">
          <p class="ut-text-sm label-premier-feature">This page is only available to premier agents.</p>
        </section>
      </template>
    </div>
</template>

<script>
import SelectList from '@/components/Forms/SelectList.vue';

import { mapGetters, mapState, mapActions } from 'vuex';
import moment from 'moment';
import ListingReferralTable from '@/components/ListingReferrals/ListingReferralTable';
import LoadingSpinner from '@/components/Animations/LoadingSpinner.vue';
import TransitionFade from '@/components/TransitionFade.vue';
import { PremierPaymentStatusTypeId, PremierPaymentStatusType } from '@/Mapper/PremierContractType';

export default {
  name: 'ListingReferrals',
  components: {
    ListingReferralTable,
    SelectList,
    LoadingSpinner,
    TransitionFade,
  },
  data() {
    return {
      timeFrameFilterOptions: [],
      selectedTimeFrame: null,
      selectedTimeFramVal: null,
      AllListingReferrals: [],
      listingReferrals: null,
      premierStartDate: Date,
    };
  },
  created() {
    this.getLeads();
    this.getPremierStartDate();
  },
  computed: {
    ...mapState({
      leadCohorts: state => state.premierLeadsListStore.leadCohorts,
    }),
    ...mapGetters('userStore', [
      'isPremier',
    ]),
    wonListings() {
      return this.$route.hash == '#won';
    },
  },
  methods: {
    ...mapActions('premierLeadsListStore', [
      'loadLeadCohort',
    ]),
    getLeads() {
      const payload = {
        userKey: this.$route.params.uk,
      };
      this.loadLeadCohort(payload).then(()=>{
        this.setListingReferrals();
      });
    },
    getPremierStartDate() {
      this.$apiServices.oa.account.getPremierStartDate(this.$route.params.uk)
        .then(this.setDatefilter, console.error)
        .catch(console.error);
    },
    updateTimeFrameFilter(value) {
      this.selectedTimeFrame = value;
      this.timeFrameFilterOptions.forEach((option) => {
        const timeframe = option;
        if (option.value === value) {
          timeframe.selected = true;
          this.selectedTimeFramVal = option.val;
        } else {
          timeframe.selected = false;
        }
      });
      this.updateListingReferrals();
    },
    updateListingReferrals() {
      return this.listingReferrals = this.AllListingReferrals.filter(lr => this.listedInTimeframe(lr.lead.stageListed) && (!this.wonListings || this.listedWithWonStage(lr.agent)));
    },
    setDatefilter(res) {
      this.timeFrameFilterOptions.push({
          val: 0,
          value: 'thisMonth',
          text: 'Current month',
          selected: false,
        });
        
      this.premierStartDate = this.$helpers.convertFromUTC(res, "MMM YYYY");
      //get months diff since the premier start date
      const monthsSincePremier = this.$helpers.getDurationDiff(this.premierStartDate, 'months');
      if(monthsSincePremier >= -5) {
        //cap 6 months back, list months in date filter
        this.generateMonthsFilter(-monthsSincePremier);
      } else {
        //add last 12 months filter for listings ealier than 6 months ago
        this.generateMonthsFilter(5);
      }
      
      this.timeFrameFilterOptions.push({
        val: -11,
        value: "Last12Months",
        text: "Last 12 Months",
        selected: true,
      });
      this.selectedTimeFrame = 'Last12Months';
      this.selectedTimeFramVal = -11;
    },
    generateMonthsFilter(monthsCountback) {
      for(var counter = 1; counter <= monthsCountback; counter++) {
          this.timeFrameFilterOptions.push({
            val: -counter,
            value: this.$helpers.getPreviousDate(new Date(), counter, "months", "MMM YYYY"),
            text: this.$helpers.getPreviousDate(new Date(), counter, "months", "MMM YYYY"),
            selected: false,
          });
        }
    },
    setListingReferrals() {
      this.listingReferrals = this.adaptApiResponse(this.leadCohorts);
    },
    listedInTimeframe(listedDate) {
      if(!listedDate)
        return false;
      //format listed date
      listedDate = this.$helpers.formatDateNormal(listedDate);
      if(this.selectedTimeFramVal !== -11) {
        return this.$helpers.getPastDateDiff(listedDate, 'months') == this.selectedTimeFramVal;
      } else {
        //Last 12 months
        return this.$helpers.getPastDateDiff(listedDate, 'months') >= this.selectedTimeFramVal;
      }
    },
    referredInLast12Months(referredDate) {
      if(!referredDate)
        return false;
      //format listed date
      referredDate = this.$helpers.formatDateNormal(referredDate);
      return this.$helpers.getDurationDiff(referredDate, 'months') > -12;
    },
    adaptApiResponse(leadsData) {
      let listingReferralsArr = [];
      leadsData.leadActivities.forEach((activity) => {
        const ref = activity;
        if(ref.referralListed) {
          ref.agent.referralDate = this.adaptDateToIso(ref.agent.referralDate);
          ref.lead.stageListed = this.adaptDateToIso(ref.lead.stageListed);
          ref.lead.fullAddress = this.adaptFullAddress(ref.lead);
          ref.agent.paymentStatus = this.getPaymentStatus(ref.agent);
          this.AllListingReferrals.push(ref);
        }
      });
      //exclude hidden listings and only show referred in last 12m listings
      this.AllListingReferrals = this.AllListingReferrals.filter(l => this.referredInLast12Months(l.agent.referralDate) && l.agent.paymentStatusTypeId != PremierPaymentStatusType.OnHold);
      this.sortListingReferrals();
      listingReferralsArr = this.AllListingReferrals.filter(lr => this.listedInTimeframe(lr.lead.stageListed) && (!this.wonListings || this.listedWithWonStage(lr.agent)));
      return listingReferralsArr;
    },
    getPaymentStatus(agent) {
      return agent.paymentStatusTypeId != null && [PremierPaymentStatusType.Paid, PremierPaymentStatusType.Invoiced].indexOf(agent.paymentStatusTypeId) > -1? 
      this.adaptPaymentStatus(agent.paymentStatusTypeId) + "<br>" + this.$helpers.formatDateNormal(this.adaptDateToIso(agent.paymentStatusDate)): null;
    },
    adaptPaymentStatus(id) {
      return PremierPaymentStatusTypeId[id];
    },
    sortListingReferrals() {
      return this.AllListingReferrals.sort((a, b) => new Date(b.lead.stageListed) - new Date(a.lead.stageListed))
    },
    adaptDateToIso(date) {
      if (date) {
        return `${date}Z`;
      }
      return null;
    },
    adaptFullAddress(lead) {
      const locality = [lead.suburb, lead.state, lead.postCode].join(' ');
      return [lead.streetAddress, locality].join(', ');
    },
    listedWithWonStage(agent)
    {
      console.log(agent.leadAgentStateTypeId);
      console.log(agent.leadAgentStageTypeId);

      return agent && agent.leadAgentStateTypeId == 1 && agent.leadAgentStageTypeId == 6;
    }
  },
};
</script>

<style scoped lang="scss">
.label-premier-feature {
  animation: fade-in .3s linear 3s 1 forwards;
  opacity: 0;
}

.listing-referrals {
  margin: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
